'use client';

import { useEffect, useState } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { useInactivityTimer } from '@/app/_lib/hooks/useInactivityTimer';
import * as Dialog from '@/app_components/ui/primitives/Dialog';
import { usePaywall } from '@/app_lib/providers/PaywallProvider';
import { useStickyFooter } from '@/app_lib/providers/StickyFooterProvider';
import { cn, unwrapLazy } from '@/app_lib/utils/utils';

const modalStyles = tv({
  base: 'h-fit w-fit z-modal-content transition-transform outline-none focus:outline-none',
  variants: {
    position: {
      center:
        'fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] data-[state=open]:fade-in data-[state=closed]:fade-out max-md:w-full',
      left: 'fixed top-0 left-0 data-[state=closed]:slide-to-left data-[state=open]:slide-from-left',
      right: 'fixed top-0 right-0 data-[state=open]:slide-from-right data-[state=closed]:slide-to-right',
    },
  },
});

type ModalVariants = VariantProps<typeof modalStyles>;

interface BasicModalProps extends ModalVariants {
  className?: string;
  content: React.ReactNode;
  controlledOpen?: boolean;
  description?: string;
  inactivityDelay?: number;
  ndrModal?: boolean;
  showOnce?: boolean;
  title?: string;
  trigger?: React.ReactNode;
  useInactivityTrigger?: boolean;
}

// TODO: Add a prop to be able to control the open/close state from outside the component
export const BasicModal = ({
  className,
  content,
  controlledOpen = false,
  description = 'This is an opened modal',
  inactivityDelay = 30000,
  ndrModal = false,
  position = 'center',
  showOnce = true,
  title = 'Modal',
  trigger,
  useInactivityTrigger = false,
}: BasicModalProps) => {
  const [open, setOpen] = useState(controlledOpen);

  const { showPaywallax } = usePaywall();
  const { renderStickyFooter } = useStickyFooter();
  const popupTimeoutShown = 15000;

  // Use inactivity timer conditionally
  const { isInactive: shouldShowFromInactivity, resetInactiveState } = useInactivityTimer({
    delay: useInactivityTrigger ? inactivityDelay : 0,
    showOnce,
  });

  // Handle inactivity trigger
  useEffect(() => {
    if (useInactivityTrigger && shouldShowFromInactivity && !open) {
      setOpen(true);
    }
  }, [shouldShowFromInactivity, useInactivityTrigger, open]);

  // Handle NDR modal timer (legacy behavior)
  useEffect(() => {
    if (ndrModal && !useInactivityTrigger) {
      const timer = setTimeout(() => {
        setOpen(true);
      }, popupTimeoutShown);

      return () => clearTimeout(timer);
    }
  }, [ndrModal, useInactivityTrigger]);

  // Handle controlled open state
  useEffect(() => {
    setOpen(controlledOpen);
  }, [controlledOpen]);

  const handleOpenChange = (newOpen: boolean) => {
    if (window.s2nApi) {
      if (!showPaywallax && renderStickyFooter) {
        window.s2nApi.float = false;
      } else {
        window.s2nApi.float = newOpen;
      }
    }
    setOpen(newOpen);

    // Reset inactive state when modal closes
    if (!newOpen && useInactivityTrigger) {
      resetInactiveState();
    }
  };

  return (
    <Dialog.Root
      onOpenChange={handleOpenChange}
      open={open}
    >
      {trigger && (
        <Dialog.Trigger
          asChild
          tabIndex={0}
        >
          {unwrapLazy(trigger)}
        </Dialog.Trigger>
      )}
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 bg-black/50 transition-opacity z-modal-backdrop',
            'data-[state=open]:fade-in data-[state=closed]:fade-out',
          )}
        />
        <Dialog.Content
          asChild
          className={cn(modalStyles({ position }), className)}
        >
          <div>
            <Dialog.Title className='sr-only'>{title}</Dialog.Title>
            <Dialog.Description className='sr-only'>{description}</Dialog.Description>
            {content}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
